// Deterministic governance core (§9.6/§9.8/§9.9 minus LLM). Given the provisional
// initial script it: (1) assigns each asset's `default` state by highest explicit
// frequency (tie → codepoint-smallest state name) and (2) materializes inherited
// state onto every entity reference so the final product is self-contained
// (§15.2). Identity merge / drop / audit are LLM stages layered on separately;
// this keep-all core always produces a valid, materialized v3 doc.

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isArr = (v: unknown): v is unknown[] => Array.isArray(v);

type StateEntry = { state_id: string; state_name: string; description: string };

// Ordered entity references discovered in a document walk. Each is a live object
// we mutate its state_id on in place.
type Ref = { assetId: string; holder: Obj; explicit: string | undefined };

function assetIndex(list: unknown): Map<string, Obj> {
  const map = new Map<string, Obj>();
  if (!isArr(list)) return map;
  for (const a of list) {
    if (!isObj(a)) continue;
    const id = a.actor_id ?? a.location_id ?? a.prop_id;
    if (typeof id === "string") map.set(id, a);
  }
  return map;
}

// Walk the doc in order, collecting entity refs (scene cast/props, scene
// location, dialogue speakers) as mutable holders plus their explicit state id.
function collectRefs(doc: Obj): Ref[] {
  const refs: Ref[] = [];
  const push = (holder: unknown, idField: string) => {
    if (!isObj(holder) || typeof holder[idField] !== "string") return;
    refs.push({ assetId: holder[idField] as string, holder, explicit: typeof holder.state_id === "string" ? holder.state_id : undefined });
  };
  for (const ep of isArr(doc.episodes) ? doc.episodes : []) {
    if (!isObj(ep) || !isArr(ep.scenes)) continue;
    for (const sc of ep.scenes) {
      if (!isObj(sc)) continue;
      push(sc.location, "location_id");
      for (const a of isArr(sc.actors) ? sc.actors : []) push(a, "actor_id");
      for (const p of isArr(sc.props) ? sc.props : []) push(p, "prop_id");
      for (const ac of isArr(sc.actions) ? sc.actions : []) {
        if (isObj(ac)) push(ac.speaker, "actor_id");
      }
    }
  }
  return refs;
}

function pickDefault(states: StateEntry[], counts: Map<string, number>): string | null {
  const candidates = states.filter((s) => (counts.get(s.state_id) ?? 0) > 0);
  if (candidates.length === 0) return null;
  let best = candidates[0]!;
  for (const s of candidates.slice(1)) {
    const c = counts.get(s.state_id) ?? 0;
    const bc = counts.get(best.state_id) ?? 0;
    if (c > bc || (c === bc && s.state_name < best.state_name)) best = s;
  }
  return best.state_id;
}

export function governDeterministic(input: unknown): Obj {
  if (!isObj(input)) throw new Error("govern expects a script object");
  const doc = JSON.parse(JSON.stringify(input)) as Obj; // pure: never mutate input

  // A speaking actor is necessarily part of the scene cast. Some upstream
  // passes intentionally rebuild sparse scene refs; restore this structural
  // fact before state materialization so validation never sees an inline actor
  // speaker detached from the scene that contains the line.
  for (const ep of isArr(doc.episodes) ? doc.episodes : []) {
    if (!isObj(ep) || !isArr(ep.scenes)) continue;
    for (const scene of ep.scenes) {
      if (!isObj(scene)) continue;
      const sceneActors = isArr(scene.actors) ? scene.actors.filter(isObj) : [];
      const actorIds = new Set(
        sceneActors
          .map((ref) => ref.actor_id)
          .filter((actorId): actorId is string => typeof actorId === "string"),
      );
      for (const action of isArr(scene.actions) ? scene.actions : []) {
        if (!isObj(action) || !isObj(action.speaker) || typeof action.speaker.actor_id !== "string") continue;
        if (actorIds.has(action.speaker.actor_id)) continue;
        sceneActors.push({ actor_id: action.speaker.actor_id });
        actorIds.add(action.speaker.actor_id);
      }
      scene.actors = sceneActors;
    }
  }

  const actors = assetIndex(doc.actors);
  const locations = assetIndex(doc.locations);
  const props = assetIndex(doc.props);
  const all = new Map<string, Obj>([...actors, ...locations, ...props]);

  const refs = collectRefs(doc);

  // 1. Count explicit state usage per asset.
  const counts = new Map<string, Map<string, number>>();
  for (const ref of refs) {
    if (ref.explicit === undefined) continue;
    if (!counts.has(ref.assetId)) counts.set(ref.assetId, new Map());
    const m = counts.get(ref.assetId)!;
    m.set(ref.explicit, (m.get(ref.explicit) ?? 0) + 1);
  }

  // 2. Per asset: pick default winner, rebuild states[] so exactly one 'default'.
  const remap = new Map<string, Map<string, string>>(); // assetId -> (oldStateId -> newStateId)
  for (const [assetId, asset] of all) {
    const states = (isArr(asset.states) ? asset.states : []).filter(isObj) as unknown as StateEntry[];
    const local = new Map<string, string>();
    const winner = pickDefault(states, counts.get(assetId) ?? new Map());
    let out: StateEntry[];
    if (winner === null) {
      // No explicit states used: keep a single synthetic default.
      const defaultState = states.find((state) => state.state_id === "default");
      out = [{
        state_id: "default",
        state_name: defaultState?.state_name ?? "default",
        description: defaultState?.description ?? "",
      }];
    } else {
      out = [];
      for (const s of states) {
        if (s.state_id === "default") continue; // drop synthetic default
        if (s.state_id === winner) { local.set(s.state_id, "default"); out.unshift({ state_id: "default", state_name: s.state_name, description: s.description ?? "" }); }
        else { local.set(s.state_id, s.state_id); out.push({ state_id: s.state_id, state_name: s.state_name, description: s.description ?? "" }); }
      }
    }
    asset.states = out;
    remap.set(assetId, local);
  }

  // 3. Materialize: walk refs in order, inherit last explicit state per asset.
  const last = new Map<string, string>();
  for (const ref of refs) {
    const local = remap.get(ref.assetId);
    let resolved: string;
    if (ref.explicit !== undefined) {
      resolved = local?.get(ref.explicit) ?? ref.explicit;
      last.set(ref.assetId, resolved);
    } else {
      resolved = last.get(ref.assetId) ?? "default";
    }
    ref.holder.state_id = resolved;
  }

  return doc;
}
